import type { Landmark, CaptureSequence, CustomLandmarks, CaptureSettings } from '../types';
import { IDX } from '../constants';
import { hexToRgba } from './display-utils';
import { getStablePelvisRedLine } from './pelvis-redline';

/** Optional hover state for landmarks/lines to show moveable feedback (larger stroke). */
export interface LandmarkHover {
  landmarkIndex: number | null;
  lineSegment: [number, number] | null;
}

const HOVER_SIZE_MULT = 1.7;
const HOVER_LINE_MULT = 1.8;
const CENTER_DOT_MULT = 0.28;
const RING_LINE_MULT = 1.6;

/** Hover ring uses `ringStroke` (typically same hue as landmark fill); center dot keeps `centerStroke` for a crisp edge. */
function drawPrecisionTarget(
  ctx: CanvasRenderingContext2D,
  p: { x: number; y: number },
  radius: number,
  centerFill: string,
  ringStroke: string,
  centerStroke: string
) {
  ctx.strokeStyle = ringStroke;
  ctx.lineWidth = Math.max(2, radius * 0.2) * RING_LINE_MULT;
  ctx.beginPath();
  ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
  ctx.stroke();

  ctx.fillStyle = centerFill;
  ctx.beginPath();
  ctx.arc(p.x, p.y, Math.max(1.5, radius * CENTER_DOT_MULT), 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = centerStroke;
  ctx.lineWidth = 1;
  ctx.stroke();
}

export const plumbAnchor = (
  view: CaptureSequence,
  lm: Landmark[],
  w: number,
  h: number,
  anchorOffsetPx = 0
) => {
  const A_L = lm?.[IDX.L_ANK], A_R = lm?.[IDX.R_ANK];
  const pickFrontalSidePoint = (side: 'L' | 'R'): Landmark | null => {
    const candidates = side === 'L'
      ? [lm?.[IDX.L_ANK], lm?.[IDX.L_HEEL], lm?.[IDX.L_FI]]
      : [lm?.[IDX.R_ANK], lm?.[IDX.R_HEEL], lm?.[IDX.R_FI]];
    const visible = candidates.find((p) => p && (!p.visibility || p.visibility > 0.3));
    if (visible) return visible;
    return (candidates.find((p) => p) ?? null) as Landmark | null;
  };
  if (view === 'Anterior' || view === 'Posterior') {
    const left = pickFrontalSidePoint('L');
    const right = pickFrontalSidePoint('R');
    if (left && right) {
      return { x: (left.x + right.x) / 2 + anchorOffsetPx, y: (left.y + right.y) / 2 };
    }
    if (A_L && A_R) return { x: (A_L.x + A_R.x) / 2 + anchorOffsetPx, y: (A_L.y + A_R.y) / 2 };
    // Avoid snapping to one side in frontal views when one leg is partially occluded.
    if (left || right) return { x: w / 2 + anchorOffsetPx, y: (left?.y ?? right?.y ?? h * 0.85) };
    return { x: w / 2 + anchorOffsetPx, y: h * 0.85 };
  }
  // For lateral views, prefer the expected side but fallback to the other
  if (view === 'Left') {
    if (A_L && (!A_L.visibility || A_L.visibility > 0.3)) return { x: A_L.x + anchorOffsetPx, y: A_L.y };
    if (A_L) return { x: A_L.x + anchorOffsetPx, y: A_L.y };
    if (A_R) return { x: A_R.x + anchorOffsetPx, y: A_R.y };
  }
  if (view === 'Right') {
    if (A_R && (!A_R.visibility || A_R.visibility > 0.3)) return { x: A_R.x + anchorOffsetPx, y: A_R.y };
    if (A_R) return { x: A_R.x + anchorOffsetPx, y: A_R.y };
    if (A_L) return { x: A_L.x + anchorOffsetPx, y: A_L.y };
  }
  return { x: w / 2 + anchorOffsetPx, y: h * 0.85 };
};

export const plumbX = (view: CaptureSequence, lm: Landmark[], w: number) => plumbAnchor(view, lm, w, 1000, 0).x;

const drawAngledPlumbLine = (
  ctx: CanvasRenderingContext2D,
  anchor: { x: number; y: number },
  floorCorrectionDeg: number
) => {
  const angleFromVerticalDeg = -floorCorrectionDeg;
  const angleRad = (angleFromVerticalDeg * Math.PI) / 180;
  const dx = Math.sin(angleRad);
  const dy = Math.cos(angleRad);
  const span = Math.hypot(ctx.canvas.width, ctx.canvas.height) * 2;
  ctx.beginPath();
  ctx.moveTo(anchor.x - dx * span, anchor.y + dy * span);
  ctx.lineTo(anchor.x + dx * span, anchor.y - dy * span);
  ctx.stroke();
};

const getPlumbDirection = (floorCorrectionDeg: number) => {
  const angleFromVerticalDeg = -floorCorrectionDeg;
  const angleRad = (angleFromVerticalDeg * Math.PI) / 180;
  return {
    dx: Math.sin(angleRad),
    dy: Math.cos(angleRad),
  };
};

export const getPlumbControlHandlePoint = (
  anchor: { x: number; y: number },
  floorCorrectionDeg: number,
  canvasWidth: number,
  canvasHeight: number,
  handlePlacement: 'top' | 'bottom' = 'top'
) => {
  const margin = Math.max(20, Math.min(canvasWidth, canvasHeight) * 0.04);
  const targetY = handlePlacement === 'top' ? margin : canvasHeight - margin;
  const { dx, dy } = getPlumbDirection(floorCorrectionDeg);
  // Line param: x = ax + dx * t, y = ay - dy * t
  const safeDy = Math.abs(dy) < 1e-5 ? (dy >= 0 ? 1e-5 : -1e-5) : dy;
  const t = (anchor.y - targetY) / safeDy;
  const x = anchor.x + dx * t;
  return {
    x: Math.max(margin, Math.min(canvasWidth - margin, x)),
    y: targetY,
  };
};

export const getPlumbEdgeControlPoints = (
  anchor: { x: number; y: number },
  floorCorrectionDeg: number,
  canvasWidth: number,
  canvasHeight: number
) => {
  const top = getPlumbControlHandlePoint(anchor, floorCorrectionDeg, canvasWidth, canvasHeight, 'top');
  const bottom = getPlumbControlHandlePoint(anchor, floorCorrectionDeg, canvasWidth, canvasHeight, 'bottom');
  return { top, bottom };
};

const drawPlumbHandle = (
  ctx: CanvasRenderingContext2D,
  handlePoint: { x: number; y: number },
  active = false
) => {
  const r = Math.max(9, ctx.canvas.width * 0.013);
  ctx.save();
  ctx.fillStyle = active ? 'rgba(251, 191, 36, 1)' : 'rgba(251, 191, 36, 0.92)';
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = Math.max(1.5, ctx.canvas.width * 0.0025);
  ctx.beginPath();
  ctx.arc(handlePoint.x, handlePoint.y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.restore();
};

const drawPlumbEdgeRail = (ctx: CanvasRenderingContext2D, y: number) => {
  ctx.save();
  ctx.strokeStyle = 'rgba(251, 191, 36, 0.35)';
  ctx.lineWidth = Math.max(1.5, ctx.canvas.width * 0.002);
  ctx.setLineDash([]);
  ctx.beginPath();
  ctx.moveTo(0, y);
  ctx.lineTo(ctx.canvas.width, y);
  ctx.stroke();
  ctx.restore();
};

const drawPerpendicularPivotTick = (
  ctx: CanvasRenderingContext2D,
  anchor: { x: number; y: number },
  floorCorrectionDeg: number
) => {
  const { dx, dy } = getPlumbDirection(floorCorrectionDeg);
  // "Up" along plumb in screen space (negative y direction).
  let ux = dx;
  let uy = -dy;
  if (uy > 0) {
    ux = -ux;
    uy = -uy;
  }
  const nx = dy; // perpendicular direction
  const ny = dx;
  // Total line length is ~50% of viewport width for floor reference.
  const halfLen = Math.max(12, ctx.canvas.width * 0.25);
  const spacing = Math.max(12, ctx.canvas.height * 0.045);
  // Center line + several above malleolus and two below.
  const offsets = [4, 3, 2, 1, 0, -1, -2];
  ctx.save();
  ctx.strokeStyle = 'rgba(251, 191, 36, 0.95)';
  ctx.lineWidth = Math.max(2, ctx.canvas.width * 0.003);
  ctx.setLineDash([]);
  for (const step of offsets) {
    const ox = anchor.x + ux * spacing * step;
    const oy = anchor.y + uy * spacing * step;
    ctx.globalAlpha = step === 0 ? 1 : 0.75;
    ctx.beginPath();
    ctx.moveTo(ox - nx * halfLen, oy - ny * halfLen);
    ctx.lineTo(ox + nx * halfLen, oy + ny * halfLen);
    ctx.stroke();
  }
  ctx.restore();
};

export function drawIdealGrid(
  ctx: CanvasRenderingContext2D,
  view: CaptureSequence,
  lm: Landmark[],
  displaySettings?: Partial<
    Pick<CaptureSettings, 'gridLineColor' | 'plumbLineColor' | 'overlayLineWidth' | 'showPlumbLine' | 'plumbLineStyle' | 'gridType'>
  >,
  floorCorrectionDeg = 0,
  anchorOffsetPx = 0,
  showPlumbHandle = false,
  handlePlacement: 'top' | 'bottom' = 'top',
  showPivotTick = false
) {
  const w = ctx.canvas.width,
    h = ctx.canvas.height;
  const anchor = plumbAnchor(view, lm, w, h, anchorOffsetPx);

  const gridColor = displaySettings?.gridLineColor || '#ffffff';
  const plumbColor = displaySettings?.plumbLineColor || '#ffff00';
  const lineWidthMult = displaySettings?.overlayLineWidth || 1.0;
  const showPlumbLine = displaySettings?.showPlumbLine !== false; // Default to true
  const plumbLineStyle = displaySettings?.plumbLineStyle || 'dashed';
  const gridType = displaySettings?.gridType || '3x3';

  ctx.save();

  // Plumb line
  if (showPlumbLine) {
    ctx.strokeStyle = hexToRgba(plumbColor, 0.95);
    ctx.lineWidth = Math.max(2, w * 0.004 * lineWidthMult);

    switch (plumbLineStyle) {
      case 'solid':
        ctx.setLineDash([]);
        break;
      case 'dotted':
        ctx.setLineDash([4, 4]);
        break;
      case 'dashed':
      default:
        ctx.setLineDash([12, 10]);
        break;
    }

    drawAngledPlumbLine(ctx, anchor, floorCorrectionDeg);
    if (showPivotTick) {
      drawPerpendicularPivotTick(ctx, anchor, floorCorrectionDeg);
    }
    if (showPlumbHandle) {
      const controls = getPlumbEdgeControlPoints(anchor, floorCorrectionDeg, w, h);
      drawPlumbEdgeRail(ctx, controls.top.y);
      drawPlumbEdgeRail(ctx, controls.bottom.y);
      drawPlumbHandle(ctx, controls.top, handlePlacement === 'top');
      drawPlumbHandle(ctx, controls.bottom, handlePlacement === 'bottom');
    }
  }

  // Grid lines (not for 'none'; '3x3' has no extra guides — plumb line only)
  if (gridType !== 'none' && gridType !== '3x3') {
    ctx.setLineDash([6, 8]);
    ctx.lineWidth = Math.max(1, w * 0.003 * lineWidthMult);
    ctx.strokeStyle = hexToRgba(gridColor, 0.85);

    if (gridType === 'golden') {
      // Golden ratio grid (horizontal lines at 38.2% and 61.8%)
      const goldenRatio = 0.618;
      const y1 = h * goldenRatio;
      const y2 = h * (1 - goldenRatio);
      
      ctx.beginPath();
      ctx.moveTo(0, y1);
      ctx.lineTo(w, y1);
      ctx.stroke();
      
      ctx.beginPath();
      ctx.moveTo(0, y2);
      ctx.lineTo(w, y2);
      ctx.stroke();
    } else if (gridType === 'center') {
      // Center crosshair only (already handled by plumb line, but add horizontal center line)
      ctx.beginPath();
      ctx.moveTo(0, h / 2);
      ctx.lineTo(w, h / 2);
      ctx.stroke();
    }
  }

  ctx.restore();
}

export function drawAP(
  ctx: CanvasRenderingContext2D, 
  landmarks: Landmark[],
  displaySettings?: Partial<Pick<CaptureSettings, 'landmarkSize' | 'landmarkFillColor' | 'landmarkStrokeColor' | 'skeletonLineWidth' | 'skeletonLineColor'>>,
  hover?: LandmarkHover | null
) {
    if (!landmarks) return;
    const w = ctx.canvas.width;
    
    const landmarkSize = displaySettings?.landmarkSize || 1.0;
    const fillColor = displaySettings?.landmarkFillColor || '#ffff00';
    const strokeColor = displaySettings?.landmarkStrokeColor || '#000000';
    const lineWidth = displaySettings?.skeletonLineWidth || 1.0;
    const lineColor = displaySettings?.skeletonLineColor || '#00ffff';
    
    const apIndices = [IDX.L_EAR, IDX.R_EAR, IDX.L_SHO, IDX.R_SHO, IDX.L_HIP, IDX.R_HIP, IDX.L_KNEE, IDX.R_KNEE, IDX.L_ANK, IDX.R_ANK];
    apIndices.forEach(i => {
      const p = landmarks[i];
      if (p) {
        const isHovered = hover?.landmarkIndex === i;
        const r = Math.max(2, w * 0.008 * landmarkSize) * (isHovered ? HOVER_SIZE_MULT : 1);
        if (isHovered) {
          const fc = hexToRgba(fillColor, 0.95);
          drawPrecisionTarget(ctx, p, r, fc, fc, strokeColor);
        } else {
          ctx.fillStyle = hexToRgba(fillColor, 0.95);
          ctx.strokeStyle = strokeColor;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
        }
      }
    });

    const segments: [number, number][] = [
      [IDX.L_SHO, IDX.R_SHO],
      [IDX.L_SHO, IDX.L_HIP], [IDX.R_SHO, IDX.R_HIP],
      [IDX.L_HIP, IDX.L_KNEE], [IDX.L_KNEE, IDX.L_ANK],
      [IDX.R_HIP, IDX.R_KNEE], [IDX.R_KNEE, IDX.R_ANK],
    ];
    const baseLineWidth = Math.max(1.5, w * 0.006 * lineWidth);
    ctx.strokeStyle = hexToRgba(lineColor, 0.85);
    segments.forEach(([i, j]) => {
      const isHovered = hover?.lineSegment && (
        (hover.lineSegment[0] === i && hover.lineSegment[1] === j) ||
        (hover.lineSegment[0] === j && hover.lineSegment[1] === i)
      );
      ctx.lineWidth = isHovered ? baseLineWidth * HOVER_LINE_MULT : baseLineWidth;
      ctx.beginPath();
      if (landmarks[i] && landmarks[j]) {
        ctx.moveTo(landmarks[i].x, landmarks[i].y);
        ctx.lineTo(landmarks[j].x, landmarks[j].y);
      }
      ctx.stroke();
    });
}

export function drawCustomPelvis(
  ctx: CanvasRenderingContext2D,
  view: CaptureSequence,
  custom: CustomLandmarks | undefined,
  displaySettings?: Partial<
    Pick<
      CaptureSettings,
      | 'landmarkSize'
      | 'skeletonLineWidth'
      | 'customLandmarkColor'
      | 'pelvisLineColor'
      | 'pelvisLineWidth'
      | 'showPelvisLine'
      | 'showPelvisLandmarks'
    >
  >,
  activePinKey?: 'ASIS' | 'PSIS' | 'L_ASIS' | 'R_ASIS' | 'L_PSIS' | 'R_PSIS' | null,
  hoverPinKey?: 'ASIS' | 'PSIS' | 'L_ASIS' | 'R_ASIS' | 'L_PSIS' | 'R_PSIS' | null,
  hoverLine?: boolean
) {
  if (!custom) return;
  const w = ctx.canvas.width;
  const landmarkSize = displaySettings?.landmarkSize || 1.0;
  const lineWidth = displaySettings?.skeletonLineWidth || 1.0;
  const pelvicColor = displaySettings?.customLandmarkColor?.trim() || '#ef4444';
  const pelvisLineColor = displaySettings?.pelvisLineColor?.trim() || '#ef4444';
  const pelvisLineW = displaySettings?.pelvisLineWidth ?? 1.0;
  const showPelvisLine = displaySettings?.showPelvisLine !== false;
  const showPelvisLandmarks = displaySettings?.showPelvisLandmarks !== false;

  const drawPelvisTarget = (
    p: { x: number; y: number },
    radius: number,
    options?: { hover?: boolean }
  ) => {
    const hover = options?.hover === true;
    const ringRadius = radius * (hover ? 1.55 : 1.35);
    ctx.strokeStyle = hover
      ? hexToRgba(pelvicColor, 0.96)
      : hexToRgba(pelvicColor, 0.9);
    ctx.lineWidth = Math.max(1.2, radius * 0.14);
    ctx.beginPath();
    ctx.arc(p.x, p.y, ringRadius, 0, Math.PI * 2);
    ctx.stroke();

    ctx.fillStyle = hexToRgba(pelvicColor, hover ? 0.92 : 0.85);
    ctx.beginPath();
    ctx.arc(p.x, p.y, Math.max(1.6, radius * 0.32), 0, Math.PI * 2);
    ctx.fill();
  };

  const drawPin = (
    p: { x: number; y: number } | undefined,
    key: 'ASIS' | 'PSIS' | 'L_ASIS' | 'R_ASIS' | 'L_PSIS' | 'R_PSIS'
  ) => {
    if (!p || !showPelvisLandmarks) return;
    const baseRadius = Math.max(2, w * 0.008 * landmarkSize);
    if (activePinKey === key || hoverPinKey === key) {
      const radius = baseRadius * HOVER_SIZE_MULT;
      drawPelvisTarget(p, radius, { hover: hoverPinKey === key && activePinKey !== key });
      return;
    }
    ctx.fillStyle = hexToRgba(pelvicColor, 0.95);
    ctx.beginPath();
    ctx.arc(p.x, p.y, baseRadius, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1;
    ctx.stroke();
  };

  const drawLine = (a?: { x: number; y: number }, b?: { x: number; y: number }) => {
    if (!a || !b || !showPelvisLine) return;
    const baseLineWidth = Math.max(1.5, w * 0.006 * lineWidth * pelvisLineW);
    ctx.strokeStyle = hexToRgba(pelvisLineColor, 0.95);
    ctx.lineWidth = hoverLine ? baseLineWidth * HOVER_LINE_MULT : baseLineWidth;
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
  };

  if (view === 'Anterior') {
    drawPin(custom.L_ASIS, 'L_ASIS');
    drawPin(custom.R_ASIS, 'R_ASIS');
    const segment = getStablePelvisRedLine(view, custom);
    if (segment) drawLine(segment.start, segment.end);
    return;
  }

  if (view === 'Posterior') {
    drawPin(custom.L_PSIS, 'L_PSIS');
    drawPin(custom.R_PSIS, 'R_PSIS');
    const segment = getStablePelvisRedLine(view, custom);
    if (segment) drawLine(segment.start, segment.end);
    return;
  }

  if (view === 'Left' || view === 'Right') {
    // Lateral: show side-specific ASIS/PSIS (legacy keys)
    drawPin(custom.ASIS, 'ASIS');
    drawPin(custom.PSIS, 'PSIS');
    const segment = getStablePelvisRedLine(view, custom);
    if (segment) drawLine(segment.start, segment.end);
  }
}

export function drawLat(
  ctx: CanvasRenderingContext2D, 
  landmarks: Landmark[], 
  side: 'Left' | 'Right', 
  custom: CustomLandmarks,
  displaySettings?: Partial<Pick<CaptureSettings, 'landmarkSize' | 'landmarkFillColor' | 'landmarkStrokeColor' | 'skeletonLineWidth' | 'skeletonLineColor' | 'customLandmarkColor'>>,
  hover?: LandmarkHover | null
) {
    if (!landmarks) return;
    const w = ctx.canvas.width;
    
    const landmarkSize = displaySettings?.landmarkSize || 1.0;
    const fillColor = displaySettings?.landmarkFillColor || '#ffff00';
    const strokeColor = displaySettings?.landmarkStrokeColor || '#000000';
    const lineWidth = displaySettings?.skeletonLineWidth || 1.0;
    const lineColor = displaySettings?.skeletonLineColor || '#00ffff';
    
    const get = (i: number) => landmarks[i];
    
    const isLeft = side === 'Left';
    const earIdx = isLeft ? IDX.L_EAR : IDX.R_EAR;
    const shoIdx = isLeft ? IDX.L_SHO : IDX.R_SHO;
    const hipIdx = isLeft ? IDX.L_HIP : IDX.R_HIP;
    const kneeIdx = isLeft ? IDX.L_KNEE : IDX.R_KNEE;
    const ankIdx = isLeft ? IDX.L_ANK : IDX.R_ANK;

    // Match analyzeLateral: only the declared side's chain (no opposite-side fallback).
    const E = get(earIdx);
    const S = get(shoIdx);
    const H = get(hipIdx);
    const K = get(kneeIdx);
    const A = get(ankIdx);

    const latPoints = [E, S, H, K, A];
    const latIndices = [earIdx, shoIdx, hipIdx, kneeIdx, ankIdx];
    const baseLineWidth = Math.max(1.5, w * 0.006 * lineWidth);
    latPoints.forEach((p, idx) => {
      if (!p) return;
      const isHovered = hover?.landmarkIndex === latIndices[idx];
      const r = Math.max(2, w * 0.008 * landmarkSize) * (isHovered ? HOVER_SIZE_MULT : 1);
      if (isHovered) {
        const fc = hexToRgba(fillColor, 0.95);
        drawPrecisionTarget(ctx, p, r, fc, fc, strokeColor);
      } else {
        ctx.fillStyle = hexToRgba(fillColor, 0.95);
        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
    });

    const latSegments: [Landmark | undefined, Landmark | undefined, number, number][] = [
      [E, S, earIdx, shoIdx], [S, H, shoIdx, hipIdx], [H, K, hipIdx, kneeIdx], [K, A, kneeIdx, ankIdx],
    ];
    ctx.strokeStyle = hexToRgba(lineColor, 0.85);
    latSegments.forEach(([p, q, i, j]) => {
      if (!p || !q) return;
      const isHovered = hover?.lineSegment && (
        (hover.lineSegment[0] === i && hover.lineSegment[1] === j) ||
        (hover.lineSegment[0] === j && hover.lineSegment[1] === i)
      );
      ctx.lineWidth = isHovered ? baseLineWidth * HOVER_LINE_MULT : baseLineWidth;
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
      ctx.lineTo(q.x, q.y);
      ctx.stroke();
    });

    // Highlight raw heel landmark and show blend connection line
    const heelIdx = isLeft ? IDX.L_HEEL : IDX.R_HEEL;
    const heel = get(heelIdx);
    if (heel && A && (!heel.visibility || heel.visibility > 0.3)) {
      ctx.save();
      ctx.strokeStyle = 'rgba(245, 158, 11, 0.6)'; // Amber-500 semi-transparent
      ctx.lineWidth = Math.max(1.5, baseLineWidth * 0.5);
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(A.x, A.y);
      ctx.lineTo(heel.x, heel.y);
      ctx.stroke();
      ctx.restore();

      const heelR = Math.max(2, w * 0.005 * landmarkSize);
      ctx.fillStyle = '#f59e0b'; // Amber-500
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(heel.x, heel.y, heelR, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }

    // Custom pelvic landmarks (show pins immediately; draw line when both exist)
    const pelvicTiltColor = '#ef4444';
    const drawPin = (p?: { x: number; y: number }) => {
      if (!p) return;
      ctx.fillStyle = pelvicTiltColor;
      ctx.beginPath();
      ctx.arc(p.x, p.y, Math.max(3, w * 0.008 * landmarkSize), 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1;
      ctx.stroke();
    };

    // Pins (visible even if only one is set)
    drawPin(custom?.ASIS);
    drawPin(custom?.PSIS);

    // Line
    if (custom?.ASIS && custom?.PSIS) {
      ctx.strokeStyle = hexToRgba(pelvicTiltColor, 0.95);
      ctx.lineWidth = Math.max(3, w * 0.006 * lineWidth);
      ctx.beginPath();
      ctx.moveTo(custom.ASIS.x, custom.ASIS.y);
      ctx.lineTo(custom.PSIS.x, custom.PSIS.y);
      ctx.stroke();
    }
}

export const drawPlumb = (
  ctx: CanvasRenderingContext2D, 
  view: CaptureSequence, 
  lm: Landmark[],
  displaySettings?: Partial<Pick<CaptureSettings, 'plumbLineColor' | 'overlayLineWidth'>>,
  floorCorrectionDeg = 0,
  anchorOffsetPx = 0,
  showPlumbHandle = false,
  handlePlacement: 'top' | 'bottom' = 'top',
  showPivotTick = false
)=>{
    const anchor = plumbAnchor(view, lm, ctx.canvas.width, ctx.canvas.height, anchorOffsetPx);
    const plumbColor = displaySettings?.plumbLineColor || '#ffff00';
    const lineWidthMult = displaySettings?.overlayLineWidth || 1.0;
    ctx.save(); 
    ctx.strokeStyle = hexToRgba(plumbColor, 0.85); 
    ctx.lineWidth = Math.max(2, ctx.canvas.width * 0.003 * lineWidthMult); 
    ctx.setLineDash([12,10]); 
    drawAngledPlumbLine(ctx, anchor, floorCorrectionDeg);
    if (showPivotTick) {
      drawPerpendicularPivotTick(ctx, anchor, floorCorrectionDeg);
    }
    if (showPlumbHandle) {
      const controls = getPlumbEdgeControlPoints(anchor, floorCorrectionDeg, ctx.canvas.width, ctx.canvas.height);
      drawPlumbEdgeRail(ctx, controls.top.y);
      drawPlumbEdgeRail(ctx, controls.bottom.y);
      drawPlumbHandle(ctx, controls.top, handlePlacement === 'top');
      drawPlumbHandle(ctx, controls.bottom, handlePlacement === 'bottom');
    }
    ctx.restore()
}

/** BlazePose skeleton segments used by drawLandmarks (for hover and drawing). */
const EAR_SHOULDER_SEGMENTS: [number, number][] = [[7, 11], [8, 12]];

export const LANDMARKS_SKELETON_SEGMENTS: [number, number][] = [
  ...EAR_SHOULDER_SEGMENTS,
  [11, 12], [12, 24], [24, 23], [23, 11],
  [11, 13], [13, 15],
  [12, 14], [14, 16],
  [23, 25], [25, 27],
  [24, 26], [26, 28],
];

/** Anterior/Posterior: omit ear–shoulder and horizontal hip; keep left–right shoulder line. */
const AP_EXCLUDED_SEGMENT_KEYS = new Set(['7-11', '8-12', '23-24']);

function isApExcludedSegment(a: number, b: number): boolean {
  const key = a < b ? `${a}-${b}` : `${b}-${a}`;
  return AP_EXCLUDED_SEGMENT_KEYS.has(key);
}

const LATERAL_LEFT_SEGMENTS: [number, number][] = [
  [IDX.L_EAR, IDX.L_SHO], [IDX.L_SHO, IDX.L_HIP], [IDX.L_HIP, IDX.L_KNEE], [IDX.L_KNEE, IDX.L_ANK],
];
const LATERAL_RIGHT_SEGMENTS: [number, number][] = [
  [IDX.R_EAR, IDX.R_SHO], [IDX.R_SHO, IDX.R_HIP], [IDX.R_HIP, IDX.R_KNEE], [IDX.R_KNEE, IDX.R_ANK],
];

/** Segments for view: side-specific in Left/Right; Anterior/Posterior use BlazePose torso/limb set minus ear–shoulder and hip horizontal. */
export function getSegmentsForView(view: CaptureSequence): [number, number][] {
  if (view === 'Left') return LATERAL_LEFT_SEGMENTS;
  if (view === 'Right') return LATERAL_RIGHT_SEGMENTS;
  return LANDMARKS_SKELETON_SEGMENTS.filter(
    ([a, b]) => !isApExcludedSegment(a, b)
  );
}

/** Unique BlazePose indices that are endpoints of the active skeleton for this view (dots + drag targets). */
export function getPoseOverlayLandmarkIndices(view: CaptureSequence): number[] {
  const set = new Set<number>();
  for (const [a, b] of getSegmentsForView(view)) {
    set.add(a);
    set.add(b);
  }
  if (view === 'Anterior' || view === 'Posterior') {
    set.add(IDX.L_EAR);
    set.add(IDX.R_EAR);
  }
  return Array.from(set).sort((x, y) => x - y);
}

/** @returns Same as {@link getPoseOverlayLandmarkIndices} (historical name). */
export function getLandmarkIndicesForView(view: CaptureSequence): number[] {
  return getPoseOverlayLandmarkIndices(view);
}

export const drawLandmarks = (
  ctx: CanvasRenderingContext2D, 
  lm: Landmark[],
  displaySettings?: Partial<Pick<CaptureSettings, 'landmarkSize' | 'landmarkFillColor' | 'landmarkStrokeColor' | 'skeletonLineWidth' | 'skeletonLineColor' | 'visibilityThreshold'>>,
  hover?: LandmarkHover | null,
  view?: CaptureSequence
)=>{
    if (!lm) return
    const w = ctx.canvas.width
    
    const landmarkSize = displaySettings?.landmarkSize || 1.0;
    const fillColor = displaySettings?.landmarkFillColor || '#00ffff';
    const strokeColor = displaySettings?.landmarkStrokeColor || '#000000';
    const lineWidth = displaySettings?.skeletonLineWidth || 1.0;
    const lineColor = displaySettings?.skeletonLineColor || '#00ffff';
    const visTh = displaySettings?.visibilityThreshold;

    const isVisible = (p: Landmark | undefined): boolean => {
      if (!p) return false;
      if (visTh === undefined) return true;
      return (p.visibility ?? 0) > visTh;
    };

    const iterIndices = view ? getPoseOverlayLandmarkIndices(view) : lm.map((_, i) => i);
    for (const i of iterIndices) {
      const p = lm[i];
      if (!isVisible(p)) continue;
      const isHovered = hover?.landmarkIndex === i;
      const r = Math.max(2, w * 0.008 * landmarkSize) * (isHovered ? HOVER_SIZE_MULT : 1);
      if (isHovered) {
        const fc = hexToRgba(fillColor, 0.9);
        drawPrecisionTarget(ctx, p, r, fc, fc, strokeColor);
      } else {
        ctx.fillStyle = hexToRgba(fillColor, 0.9);
        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
    }
    const baseLineWidth = Math.max(1.5, w * 0.005 * lineWidth);
    ctx.strokeStyle = hexToRgba(lineColor, 0.8);
    const segments = view ? getSegmentsForView(view) : LANDMARKS_SKELETON_SEGMENTS;
    segments.forEach(([i, j]) => {
      if (!isVisible(lm[i]) || !isVisible(lm[j])) return;
      const isHovered = hover?.lineSegment && (
        (hover.lineSegment[0] === i && hover.lineSegment[1] === j) ||
        (hover.lineSegment[0] === j && hover.lineSegment[1] === i)
      );
      ctx.lineWidth = isHovered ? baseLineWidth * HOVER_LINE_MULT : baseLineWidth;
      ctx.beginPath();
      ctx.moveTo(lm[i]!.x, lm[i]!.y);
      ctx.lineTo(lm[j]!.x, lm[j]!.y);
      ctx.stroke();
    });

    // Highlight raw heel landmark and show blend connection line for lateral views
    if (view && (view === 'Left' || view === 'Right')) {
      const isLeft = view === 'Left';
      const ankIdx = isLeft ? IDX.L_ANK : IDX.R_ANK;
      const heelIdx = isLeft ? IDX.L_HEEL : IDX.R_HEEL;
      const ankle = lm[ankIdx];
      const heel = lm[heelIdx];
      if (ankle && heel && isVisible(ankle) && isVisible(heel)) {
        ctx.save();
        ctx.strokeStyle = 'rgba(245, 158, 11, 0.6)'; // Amber-500 semi-transparent
        ctx.lineWidth = Math.max(1.5, baseLineWidth * 0.5);
        ctx.setLineDash([4, 4]);
        ctx.beginPath();
        ctx.moveTo(ankle.x, ankle.y);
        ctx.lineTo(heel.x, heel.y);
        ctx.stroke();
        ctx.restore();

        const heelR = Math.max(2, w * 0.005 * landmarkSize);
        ctx.fillStyle = '#f59e0b'; // Amber-500
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(heel.x, heel.y, heelR, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
    }
}

export const drawInteractivePlumb = (
  ctx: CanvasRenderingContext2D,
  x: number,
  floorCorrectionDeg = 0,
  anchorY = ctx.canvas.height * 0.85,
  showHandle = true
) => {
    ctx.save(); 
    ctx.strokeStyle='rgba(255,255,0,0.9)'; 
    ctx.lineWidth=Math.max(3, ctx.canvas.width*0.004); 
    ctx.setLineDash([15,10]); 
    ctx.shadowColor = "rgba(0,0,0,0.8)";
    ctx.shadowBlur = 5;
    const anchor = { x, y: anchorY };
    drawAngledPlumbLine(ctx, anchor, floorCorrectionDeg);
    
    // Handle at top
    const angleFromVerticalDeg = -floorCorrectionDeg;
    const angleRad = (angleFromVerticalDeg * Math.PI) / 180;
    const dx = Math.sin(angleRad);
    const dy = Math.cos(angleRad);
    const span = Math.hypot(ctx.canvas.width, ctx.canvas.height) * 2;
    const tipX = anchor.x - dx * span;
    const tipY = anchor.y + dy * span;
    const perpX = Math.cos(angleRad);
    const perpY = -Math.sin(angleRad);
    const halfWidth = 10;
    const depth = 15;

    ctx.fillStyle = '#fbbf24';
    ctx.beginPath();
    ctx.moveTo(tipX - perpX * halfWidth, tipY - perpY * halfWidth);
    ctx.lineTo(tipX + perpX * halfWidth, tipY + perpY * halfWidth);
    ctx.lineTo(tipX + dx * depth, tipY - dy * depth);
    ctx.fill();
    if (showHandle) {
      drawPlumbHandle(ctx, anchor, true);
    }
    ctx.restore();
}

const VIEW_CORNER_MARKERS: Record<CaptureSequence, { left: string; right: string }> = {
  Anterior: { left: 'R', right: 'L' },
  Posterior: { left: 'L', right: 'R' },
  Right: { left: 'P', right: 'A' },
  Left: { left: 'A', right: 'P' },
};

/** Draw top-corner orientation labels for the active capture view. Pass `imageSize` when the context is scaled so landmark coords match original image pixels. */
export function drawViewportOrientationLabels(
  ctx: CanvasRenderingContext2D,
  view: CaptureSequence,
  imageSize?: { width: number; height: number },
  laymanMode?: boolean
): void {
  const iw = imageSize?.width ?? ctx.canvas.width;
  const ih = imageSize?.height ?? ctx.canvas.height;
  if (iw <= 0 || ih <= 0) return;
  let marker = VIEW_CORNER_MARKERS[view];
  if (!marker) return;

  if (laymanMode) {
    const mapKey = (val: string) => {
      if (val === 'A') return 'F';
      if (val === 'P') return 'B';
      return val;
    };
    marker = {
      left: mapKey(marker.left),
      right: mapKey(marker.right),
    };
  }

  ctx.save();
  const pad = Math.max(24, iw * 0.06);
  const fontPx = Math.max(48, Math.floor(iw * 0.14));
  ctx.font = `700 ${fontPx}px system-ui, -apple-system, sans-serif`;
  ctx.fillStyle = 'rgba(255, 255, 255, 0.98)';
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.82)';
  ctx.lineWidth = Math.max(4, iw * 0.004);
  ctx.textBaseline = 'top';

  ctx.textAlign = 'left';
  ctx.strokeText(marker.left, pad, pad);
  ctx.fillText(marker.left, pad, pad);

  ctx.textAlign = 'right';
  ctx.strokeText(marker.right, iw - pad, pad);
  ctx.fillText(marker.right, iw - pad, pad);
  ctx.restore();
}
